<template>
  <div class="column-edit-view-wrapper">
    <a-tabs v-model:activeKey="activeTab">
      <a-tab-pane
        key="columns"
        :tab="component_type === 'normal' ? $t('列设置') : $t('字段设置')"
      >
        <div class="tab-content-scroll">
          <pivot-config-comp
            v-if="component_type === 'pivot_table'"
            ref="pivotConfigRef"
            :columns="columns"
            :modelValue="pivotConfig"
            :settingMode="settingMode"
            :settingDrawerWidth="settingDrawerWidth"
            :settingDrawerPlacement="settingDrawerPlacement"
            @change="onPivotConfigChange"
            @openValueConfig="onOpenValueConfig"
          />
          <pivot-charts-config-comp
            v-else-if="component_type === 'pivot_charts'"
            ref="pivotChartsConfigRef"
            :columns="columns"
            :modelValue="pivotChartsConfig"
            :settingMode="settingMode"
            :settingDrawerWidth="settingDrawerWidth"
            :settingDrawerPlacement="settingDrawerPlacement"
            @change="onPivotChartsConfigChange"
            @openValueConfig="onOpenValueConfig"
          />
          <columns-config
            v-else-if="component_type === 'normal'"
            :columns="columns"
            @change="onColumnChange"
          />
        </div>
      </a-tab-pane>
      <a-tab-pane
        key="views"
        :tab="$t('视图管理')"
        v-if="is_view"
      >
        <div class="tab-content-scroll">
          <views-config
            :columns="columns"
            :view_key="view_key"
            :view_flag_name="view_flag_name"
            :is_view="is_view"
          />
        </div>
      </a-tab-pane>
    </a-tabs>
  </div>
  <a-drawer
    v-if="settingMode === 'drawer'"
    :title="valueFieldTitle"
    :width="settingDrawerWidth"
    :placement="settingDrawerPlacement"
    :push="false"
    :mask="false"
    v-model:open="valueSettingOpen"
  >
    <value-field-config
      :valueField="currentValueField"
      :field-options="fieldOptions"
      :sub-expressions="currentSubExpressions"
      @change="onValueFieldChange"
    />
  </a-drawer>
  <a-modal
    v-else
    :title="valueFieldTitle"
    width="900px"
    :body-style="{ height: 'calc(100vh - 250px)' }"
    v-model:open="valueSettingOpen"
  >
    <value-field-config
      :valueField="currentValueField"
      :field-options="fieldOptions"
      :sub-expressions="currentSubExpressions"
      @change="onValueFieldChange"
    />
    <template #footer>
      <a-button @click="valueSettingOpen = false">
        {{ $t('关闭') }}
      </a-button>
    </template>
  </a-modal>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import {
  Column,
  PivotConfig,
  PivotChartsConfig,
  SettingMode,
  PivotValueField,
  PivotFieldOption,
  PivotSubExpression,
  SettingDrawerPlacement,
} from '../typing';
import ColumnsConfig from './columnsConfig.vue';
import PivotConfigComp from './pivotConfig.vue';
import PivotChartsConfigComp from './pivotChartsConfig.vue';
import ViewsConfig from './viewsConfig.vue';
import valueFieldConfig from './valueFieldConfig.vue';
import { useTableProState } from '../utils';

interface Props {
  columns: Array<Column>;
  view_key: string;
  view_flag_name: string;
  is_view: boolean;
  component_type: string;
  pivotConfig: PivotConfig | null;
  pivotChartsConfig: PivotChartsConfig | null;
  settingMode: SettingMode;
  settingDrawerWidth: string | number;
  settingDrawerPlacement: SettingDrawerPlacement;
}

const props = defineProps<Props>();
const emit = defineEmits(['change', 'pivotConfigChange', 'pivotChartsConfigChange']);

const activeTab = ref('columns');
const valueSettingOpen = ref(false);
const currentValueField = ref<PivotValueField | null>(null);
// 高级配置抽屉关联的源组件（用于把抽屉内的修改回写到对应配置并向上 emit）
const currentValueFieldSource = ref<'pivot' | 'charts' | null>(null);
const pivotConfigRef = ref<InstanceType<typeof PivotConfigComp> | null>(null);
const pivotChartsConfigRef = ref<InstanceType<typeof PivotChartsConfigComp> | null>(null);

// 左侧字段列表（叶子列）映射为 {field, label}
const fieldOptions = computed<PivotFieldOption[]>(() => {
  const buildTree = (cols: Column[]): PivotFieldOption[] => {
    return cols
      .map(col => {
        const label = col.title || col.dataIndex || col.key || '';
        const children = col.children?.length ? buildTree(col.children) : [];
        const isLeaf = children.length === 0;
        if (isLeaf) {
          if (!col.dataIndex || col.dataIndex === 'index' || col.dataIndex === 'action') {
            return null;
          }
          return {
            field: col.dataIndex,
            label,
          };
        }
        return {
          field: col.dataIndex || col.key || label,
          label,
          children,
        };
      })
      .filter(Boolean) as PivotFieldOption[];
  };
  return buildTree(props.columns || []);
});

const flatFieldOptions = computed<PivotFieldOption[]>(() => {
  const flatten = (fields: PivotFieldOption[]): PivotFieldOption[] => {
    const list: PivotFieldOption[] = [];
    fields.forEach(field => {
      if (field.children?.length) {
        list.push(...flatten(field.children));
      } else {
        list.push({ field: field.field, label: field.label });
      }
    });
    return list;
  };
  return flatten(fieldOptions.value);
});

// 抽屉标题：表达式型显示「表达式 + 预览片段」，否则显示字段名
const valueFieldTitle = computed(() => {
  const v = currentValueField.value;
  if (!v) return '';
  if (v.useExpression && (v.expression || []).length) {
    const preview = (v.expression || [])
      .map(t => {
        if (t.type === 'field') {
          const f = flatFieldOptions.value.find(x => x.field === t.value);
          return f ? f.label : t.value;
        }
        if (t.type === 'operator') return t.value;
        if (t.type === 'number') return t.value;
        if (t.type === 'lparen') return '(';
        if (t.type === 'rparen') return ')';
        return '';
      })
      .join(' ');
    return `${
      props.component_type === 'pivot_charts' ? '透视图' : '透视表'
    } · ${'表达式'}: ${preview}`;
  }
  return `${'字段设置'}: ${v.label || v.field || ''}`;
});

const onColumnChange = (data: any) => {
  emit('change', data);
};
const onPivotConfigChange = (data: PivotConfig) => {
  emit('pivotConfigChange', data);
};
const onPivotChartsConfigChange = (data: PivotChartsConfig) => {
  emit('pivotChartsConfigChange', data);
};
const { fetchViews } = useTableProState();
watch(
  () => activeTab.value,
  val => {
    if (val === 'views') {
      fetchViews();
    }
  },
);

// 高级配置抽屉内的修改（表达式开关 / token 增删）由 valueFieldConfig 直接 mutate 共享对象，
// 这里通过源组件暴露的 forceEmit 触发一次完整配置回传，确保后端重算与持久化拿到最新值。
const onValueFieldChange = () => {
  if (currentValueFieldSource.value === 'charts') {
    pivotChartsConfigRef.value?.forceEmit?.();
  } else {
    pivotConfigRef.value?.forceEmit?.();
  }
};

const onOpenValueConfig = (field: PivotValueField) => {
  currentValueField.value = field;
  currentValueFieldSource.value = props.component_type === 'pivot_charts' ? 'charts' : 'pivot';
  valueSettingOpen.value = true;
};

// 当前配置中可引用的子表达式列表。
// 注意：直接读 props.pivotConfig/pivotChartsConfig 只能拿到上层回灌的快照，
// 会话内新增/编辑的子表达式（localConfig）不会实时同步到 props，导致「高级配置」里
// 的表达式编辑器看不到已配置的子表达式。因此改为直接读取对应子组件暴露的实时列表。
const currentSubExpressions = computed<PivotSubExpression[]>(() => {
  const compRef =
    props.component_type === 'pivot_charts' ? pivotChartsConfigRef.value : pivotConfigRef.value;
  return compRef?.getSubExpressions?.() || [];
});
</script>

<style lang="less" scoped>
.column-edit-view-wrapper {
  padding: 0 8px;
  height: 100%;
}
.tab-content-scroll {
  overflow-y: auto;
  height: 100%;
}
:deep(.ant-tabs) {
  height: 100%;
}
:deep(.ant-tabs-content) {
  height: 100%;
}
</style>
